/**
 * ================================================================================================
 *  בקשות שינוי — what customers have asked for, and nobody has answered yet
 * ================================================================================================
 *
 * The Owner, 2026-11-02:
 *
 *     *"לקוח בעל תנאי מסחרי מיוחד — מסכים למסלול בקשה כאשר השינוי אינו ניתן לביצוע תוך שימור
 *      התנאים שלו. אין לבטל או להחליף תנאי מוסכם בשקט."*
 *
 * ── ANSWERING A REQUEST CHANGES NO TERMS, AND THIS SCREEN SAYS SO ─────────────────────────────
 *
 * **That is his second sentence, and it is the hardest thing for an interface to keep.** The
 * tempting screen is one where an operator reads *"we agreed to move them to the annual plan"*
 * and presses a button that moves them. This one does not have that button, because the service
 * behind it holds no commercial store: resolving writes an ANSWER, and moving a customer is a
 * separate attributed act on the customer's own card, where the plan change has its own
 * confirmation and its own audit row.
 *
 * So the confirmation here says, in words, that the terms are unchanged — and the link to the
 * customer card is where the change is actually made.
 *
 * ── OLDEST FIRST ──────────────────────────────────────────────────────────────────────────────
 *
 * The opposite order from the office's own history, deliberately: a customer reads newest-first
 * because the last thing they asked is what they are wondering about, and an operator works a
 * queue from the end that has waited longest.
 */

import { useCallback, useState } from 'react';

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { InfoNote } from '../components/InfoNote';
import { Modal } from '../components/Modal';
import { ServerRead } from '../components/ServerRead';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { callOperation } from '../api/operations';
import { classifyActionError } from '../api/actionOutcome';
import type { ActionFailure } from '../api/actionOutcome';
import type { ChangeRequestRead } from '../api/contract';
import { useServerData } from '../api/useServerData';
import { routes } from '../routes';

const TITLE_HE = 'בקשות שינוי';

/** `CK_ccr_kind`'s four, in words an operator triages by. */
const KIND_WORDS: Readonly<Record<string, string>> = {
  plan_change: 'מעבר לתכנית',
  quantity_change: 'שינוי כמויות',
  module: 'מודול',
  other: 'אחר',
};

type Answer = { readonly toStatus: 'in_progress' | 'resolved' | 'declined'; readonly row: ChangeRequestRead };

export function PlatformChangeRequestsScreen(): JSX.Element {
  const ctx = useWorkContext();
  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }
  return <PlatformChangeRequestsBody />;
}

function PlatformChangeRequestsBody(): JSX.Element {
  const read = useCallback(() => callOperation('platform.change_request.read', {}), []);
  const queue = useServerData(read);

  const [answering, setAnswering] = useState<Answer | null>(null);
  const [resolution, setResolution] = useState('');
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<ActionFailure | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const back = { labelHe: 'מרכז החיוב', href: routes.platformCharges };

  async function answer(a: Answer): Promise<void> {
    setSending(true);
    setFailure(null);
    try {
      await callOperation('platform.change_request.resolve', {
        params: { change_request_uid: a.row.changeRequestUid },
        body: {
          /*
           * THE STATUS FROM THE ROW ON SCREEN, which the store puts in the WHERE. Two operators
           * answering one request produce one answer and one `already_moved`, rather than the
           * second overwriting what the customer was already told.
           */
          expectedStatus: a.row.status,
          toStatus: a.toStatus,
          resolution: a.toStatus === 'in_progress' ? null : resolution.trim(),
        },
      });
      setDone(a.row.changeRequestUid);
      queue.reload();
    } catch (err) {
      setFailure(classifyActionError(err));
    } finally {
      setSending(false);
      setAnswering(null);
      setResolution('');
    }
  }

  return (
    <AppShell titleHe={TITLE_HE} backToParent={back}>
      <InfoNote summaryHe="מה המסך הזה עושה, ומה הוא במפורש אינו עושה">
        <p>
          כאן נרשמות בקשות של לקוחות לשינוי מסחרי שאינם יכולים לבצע בעצמם — מעבר לתכנית, שינוי
          כמויות, מודול או תנאי מיוחד. הרשימה מסודרת מהוותיקה ביותר.
        </p>
        <p>
          <strong>מענה לבקשה אינו משנה את תנאי הלקוח.</strong> הוא רושם מה נענה ומי ענה. שינוי
          תכנית, כמות או הנחה נעשה בכרטיס הלקוח, כפעולה נפרדת עם אישור ותיעוד משלה — כדי שלא ייווצר
          מצב שבו תנאי מוסכם משתנה בלי שמישהו ביצע את השינוי במפורש.
        </p>
        <p>
          הבקשה אינה נושאת סכום. הלקוח מתאר מה הוא צריך; התמחור נקבע בספר המחירים ובמסלול ההנחות,
          שם כבר קיימות הבקרות.
        </p>
      </InfoNote>

      {failure === null ? null : (
        /*
         * NO `onStepUpSatisfied`, AND `stepUpSeam.test.ts` IS RIGHT TO INSIST. The seam exists
         * for a screen whose write can PROVOKE a second-factor demand, and neither of these
         * can: `platform.subscription.edit` carries no step-up, because answering a customer
         * about their terms chooses no amount and moves no money. A handler for a demand this
         * screen cannot receive is dead code that reads as a guarantee.
         */
        <ActionFailureNotice failure={failure} />
      )}
      {done === null ? null : (
        <p className="notice__body" data-area="answered">
          הבקשה עודכנה. תנאי הלקוח לא שונו — שינוי מתבצע בכרטיס הלקוח.
        </p>
      )}

      <ServerRead result={queue.result} whatHe="את הבקשות הפתוחות" skeletonRows={4}>
        {(data) =>
          data.requests.length === 0 ? (
            <StateBlock
              titleHe="אין בקשות פתוחות"
              bodyHe="כל הבקשות נענו. בקשה חדשה תופיע כאן ברגע שלקוח ישלח אותה."
              tone="empty"
            />
          ) : (
            <section className="notice" data-tone="info" data-area="change-requests">
              <h2 className="notice__title">{`בקשות פתוחות — ${data.requests.length}`}</h2>
              <div className="grid">
                <div className="grid__scroll">
                  <table className="grid__table">
                    <caption className="grid__caption">הוותיקה למעלה</caption>
                    <thead className="grid__head">
                      <tr>
                        <th scope="col">לקוח</th>
                        <th scope="col">נשלחה</th>
                        <th scope="col">סוג</th>
                        <th scope="col">מה התבקש</th>
                        <th scope="col">מצב</th>
                        <th scope="col">פעולה</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.requests.map((row) => (
                        <tr key={row.changeRequestUid}>
                          <td>
                            <a href={routes.platformTenant(row.tenantUid)}>{row.tenantLegalName}</a>
                          </td>
                          <td>
                            <span dir="ltr">{row.createdAtUtc.slice(0, 10)}</span>
                          </td>
                          <td>{KIND_WORDS[row.requestKind] ?? row.requestKind}</td>
                          <td>{row.detail}</td>
                          <td>
                            <StatusChip
                              labelHe={row.status === 'open' ? 'ממתינה' : 'בטיפול'}
                              tone="warn"
                            />
                          </td>
                          <td>
                            {row.status === 'open' ? (
                              <button
                                type="button"
                                className="btn"
                                onClick={() => setAnswering({ toStatus: 'in_progress', row })}
                              >
                                לקיחה לטיפול
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="btn"
                              onClick={() => setAnswering({ toStatus: 'resolved', row })}
                            >
                              מענה
                            </button>
                            <button
                              type="button"
                              className="btn"
                              onClick={() => setAnswering({ toStatus: 'declined', row })}
                            >
                              דחייה
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )
        }
      </ServerRead>

      {answering === null ? null : (
        <Modal
          intentHe={
            answering.toStatus === 'in_progress'
              ? 'לקיחת בקשה לטיפול'
              : answering.toStatus === 'resolved'
                ? 'מענה לבקשה'
                : 'דחיית בקשה'
          }
          /*
           * 09 §2.2 — and the sentence that matters most on this screen is the one about what
           * does NOT happen. An operator who has just agreed something with a customer is
           * precisely the person who might read "resolved" as "done".
           */
          blastRadiusHe={
            answering.toStatus === 'in_progress'
              ? `הבקשה של ${answering.row.tenantLegalName} תסומן כבטיפול. הלקוח יראה זאת. לא נשלח מסמך ולא משתנה דבר בתנאים.`
              : `התשובה תירשם ותוצג ללקוח ${answering.row.tenantLegalName}, בשמך ובתאריך. ` +
                'תנאי הלקוח אינם משתנים בפעולה הזאת: שינוי תכנית, כמות או הנחה נעשה בכרטיס הלקוח, בנפרד.'
          }
          fields={answering.toStatus === 'in_progress' ? 0 : 1}
          confirmLabelHe={sending ? 'שומר…' : 'אישור'}
          onConfirm={() => {
            if (answering.toStatus === 'in_progress' || resolution.trim() !== '') {
              void answer(answering);
            }
          }}
          onClose={() => {
            setAnswering(null);
            setResolution('');
          }}
        >
          {answering.toStatus === 'in_progress' ? (
            <p className="form__note">אין צורך בתשובה בשלב הזה.</p>
          ) : (
            <>
              <label className="field">
                <span className="field__label">התשובה ללקוח</span>
                <textarea
                  className="field__input"
                  name="change-request-resolution"
                  value={resolution}
                  maxLength={2000}
                  onChange={(e) => setResolution(e.currentTarget.value)}
                />
                <span className="field__hint">
                  הלקוח קורא את זה. בדחייה — מה הסיבה; במענה — מה סוכם ומה הצעד הבא.
                </span>
              </label>
              {/*
                A CLOSED REQUEST SAYS SOMETHING. `CK_ccr_resolved_has_answer` refuses the row and
                the service refuses it by name; this stops the press before either, so the
                operator sees it beside the field.
              */}
              {resolution.trim() !== '' ? null : (
                <p className="form__note">הפעולה אינה נשלחת עד שנכתבה תשובה.</p>
              )}
            </>
          )}
        </Modal>
      )}
    </AppShell>
  );
}
